import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchChatState } from './chat.ts';

/**
 * Where the drawer stands. On the sitting the backend says is running; on one
 * the user chose (a first message, or picking one back up out of the history);
 * or on a conversation that does not exist yet, on purpose: the user asked for
 * a blank drawer, or the page put its opening question in front of them.
 * Nothing is created or closed by standing on a new one — the sitting that was
 * running is still the running one until something is said.
 */
type Standing = { on: 'running' } | { on: 'chosen'; id: string } | { on: 'new' };

export interface DrawerConversation {
  /** The conversation on screen, or null where the drawer stands on none. */
  conversationId: string | null;
  /**
   * The drawer stands on no conversation. One name for the three ways of
   * arriving there — asked for, offered, or none running — because what the
   * drawer does next hangs on the state and not on how it was reached.
   */
  blank: boolean;
  /** Whether the drawer stands on a new conversation on purpose. */
  startingNew: boolean;
  /** Stand on a conversation the user picked. */
  open: (conversationId: string) => void;
  /** Stand on a conversation that does not exist yet. */
  startNew: () => void;
  /** Something was said, and the answer names the conversation it went to. */
  spokeIn: (conversationId: string) => void;
}

/**
 * Which conversation the drawer is in.
 *
 * Whether a sitting is still running is the backend's judgement, asked each
 * time the drawer opens. From then on this names the conversation it is in, so
 * that moving around the app — or a long silence with the drawer open — cannot
 * start a new one underneath the user.
 */
export function useDrawerConversation(drawerOpen: boolean): DrawerConversation {
  const queryClient = useQueryClient();
  const { data: state } = useQuery({
    queryKey: ['chatState'],
    queryFn: fetchChatState,
    /* Asked again on each open: a sitting can go quiet while the app is left alone. */
    enabled: drawerOpen,
    staleTime: 0,
  });

  const [standing, setStanding] = useState<Standing>({ on: 'running' });

  const conversationId = conversationOf(standing, state?.conversation?.id ?? null);

  const open = useCallback((id: string) => setStanding({ on: 'chosen', id }), []);
  const startNew = useCallback(() => setStanding({ on: 'new' }), []);

  const spokeIn = (id: string) => {
    /* The first message of a sitting is what creates it. */
    if (id !== conversationId) {
      open(id);
    }

    /* Speaking changes both which sitting is current and what the history says. */
    void queryClient.invalidateQueries({ queryKey: ['chatState'] });
    void queryClient.invalidateQueries({ queryKey: ['chatHistory'] });
  };

  return {
    conversationId,
    blank: conversationId === null,
    startingNew: standing.on === 'new',
    open,
    startNew,
    spokeIn,
  };
}

function conversationOf(standing: Standing, running: string | null): string | null {
  switch (standing.on) {
    case 'running':
      return running;
    case 'chosen':
      return standing.id;
    case 'new':
      return null;
  }
}
